import { useContext, useEffect, useState } from 'react';

import {
  Box,
  Button,
  FieldSet,
  FormHelp,
  Heading,
  IconButton,
  Input,
  InputChip,
  Label,
  Legend,
  Paragraph,
  Popover,
} from '@zillow/constellation-10';
import { IconLockClosedOutline, IconPlusCircleFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyAmenityWebApiHandler_create,
  useRentalManagerUserPropertyAmenityWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import useMultiplexListingFlow from '../../../hooks/useMultiplexListingFlow';
import { useSelector } from '../../../selectors/useSelector';

import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';

import VisuallyHiddenScreenReader from '../../../components/VisuallyHiddenScreenReader';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';
import type { LegendPropsInterface } from '@zillow/constellation-10';
import type { ApartmentAmenityProps } from '../../../model/ApartmentAmenity';

const LOCK_BUTTON_TOOLTIP_CONTENT = {
  title: 'Need to edit these amenities?',
  body: 'These amenities are coming from your feed and must be edited in that system.',
};

const ADD_BUTTON_TOOLTIP_CONTENT = {
  title: 'Adding additional amenities',
  body: 'Added amenities will only display on Zillow Rental Network and must be edited or removed here.',
};

type TagProps = Omit<ApartmentAmenityProps, 'availableInSelectUnits'>;

type Props = {
  errorObj: { additionalAmenities?: string };
  isInputFormDisabled?: boolean;
  label: string;
  labelSize?: LegendPropsInterface['size'];
  listingAlias: string;
  previouslySavedAmenities: Array<TagProps>;
  updateFormData: (arg: { propertyAmenities: Array<TagProps> }) => void;
  updateErrorObj: (arg: { additionalAmenities?: string }) => void;
  updateFetchingStatus?: (v: boolean) => void;
};

const AdditionalAmenities = ({
  errorObj,
  isInputFormDisabled = false,
  label,
  labelSize = 'md',
  listingAlias,
  previouslySavedAmenities = [],
  updateFormData,
  updateErrorObj,
  updateFetchingStatus = () => {},
}: Props): JSX.Element => {
  const queryClient = useQueryClient();

  const {
    modularListingState: { isInModalState },
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const {
    currentProperty: {
      isFeed,
      restrictions: { isPropertyAmenitiesUpdateRestricted },
    },
  } = useSelector((state) => state.properties);

  const [tags, setTags] = useState([...previouslySavedAmenities]);
  const [tagToAdd, setTagToAdd] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicateTagError, setDuplicateTagError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { mutate: createPropertyAmenity } = useRentalManagerUserPropertyAmenityWebApiHandler_create({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserPropertyAmenityWebApiHandler_create',
      },
      invalidates: ['properties-amenities-create'],
    },
  });

  const { mutate: deletePropertyAmenity } = useRentalManagerUserPropertyAmenityWebApiHandler_delete({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserPropertyAmenityWebApiHandler_delete',
      },
      invalidates: ['properties-amenities-delete'],
    },
  });

  const hasTags = tags.length > 0;

  useEffect(() => {
    updateFormData({ propertyAmenities: tags });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const addNewTagOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (duplicateTagError) {
      setDuplicateTagError(false);
    }
    setTagToAdd(e.target.value);
  };

  const createAmenityOnBlurHandler = () => {
    if (tagToAdd === '') {
      updateErrorObj({ additionalAmenities: undefined });
    }
  };

  const createAmenity = () => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    setSuccessMessage('');

    setDuplicateTagError(false);

    if (!tagToAdd) {
      updateErrorObj({ additionalAmenities: 'Property amenity cannot be blank.' });
      return;
    }
    updateErrorObj({ additionalAmenities: undefined });

    const isDuplicateOfSavedTag = tags.some((tag) => tag.amenity === tagToAdd);

    if (isDuplicateOfSavedTag) {
      setDuplicateTagError(true);
      return;
    }

    updateFetchingStatus(true);

    createPropertyAmenity(
      {
        getParams: {
          listingAlias,
          text: tagToAdd,
        },
      },
      {
        onSuccess: (result) => {
          setSuccessMessage(`Successfully added ${tagToAdd}.`);
          updateErrorObj({ additionalAmenities: undefined });
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          setTags([...tags, { id: result.amenityId, amenity: tagToAdd, isEditable: true }]);
          setTagToAdd('');
        },
        onError: (error) => {
          updateErrorObj({ additionalAmenities: DatarouterError.getDisplayMessage(error) });
          logError({
            error,
            context: 'PropertyAmenities.createAmenity',
          });
        },
        onSettled: () => {
          updateFetchingStatus(false);
        },
      },
    );
  };

  const deleteAmenity = (index: number) => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    setSuccessMessage('');

    const tagToDelete = tags[index];

    updateFetchingStatus(true);
    setIsDeleting(true);

    deletePropertyAmenity(
      {
        getParams: {
          amenityId: tagToDelete.id,
        },
      },
      {
        onSuccess: () => {
          setSuccessMessage(`Successfully deleted ${tagToDelete.amenity}.`);
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          const tagsCopy = [...tags];
          tagsCopy.splice(index, 1);
          setTags(tagsCopy);
        },
        onError: (error) => {
          const clientErrMsg = 'There was an error deleting your amenity. Please try again.';
          updateErrorObj({ additionalAmenities: clientErrMsg });
          logError({
            error,
            errorType: 'AdditionalAmenitiesError',
            context: 'PropertyAmenities.deleteAmenity',
          });
        },
        onSettled: () => {
          updateFetchingStatus(false);
          setIsDeleting(false);
        },
      },
    );
  };

  const handleCreateAmenityKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 13 && isInModalState) {
      createAmenity();
    }
  };

  return (
    <FieldSet
      legend={
        <>
          {hasTags ? (
            <Box css={{ display: 'flex', flexDirection: 'row', gap: '200', alignItems: 'center', marginBottom: '400' }}>
              <Legend size={labelSize} disabled={isInputFormDisabled}>
                {label}
              </Legend>

              {isFeed && (
                <Popover
                  header={
                    <Heading level={2} textStyle="body-lg-bold">
                      {LOCK_BUTTON_TOOLTIP_CONTENT.title}
                    </Heading>
                  }
                  body={<Paragraph>{LOCK_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                  placement="top"
                  trigger={
                    <IconButton
                      size="sm"
                      title="Lock outline"
                      tone="neutral"
                      emphasis="bare"
                      icon={<IconLockClosedOutline />}
                    />
                  }
                />
              )}
            </Box>
          ) : (
            <Box css={{ marginBottom: '400' }} />
          )}
        </>
      }
    >
      {hasTags && (
        <ul>
          {tags.map((tag) =>
            !tag.isEditable ? (
              <li key={`feed-amenity-tag-${tag.id}`}>
                <Paragraph css={{ marginBottom: '200', color: 'gray.400' }}>{tag.amenity}</Paragraph>
              </li>
            ) : null,
          )}
          <Box css={{ marginBottom: '200' }} />
        </ul>
      )}

      {hasTags && (
        <ul>
          {tags.map((tag, i) => (
            <li key={`amenity-tag-${tag.id}`}>
              {/* TODO: create delete queue */}
              <InputChip
                disabled={!tag.isEditable}
                isOpen={tag.isEditable}
                onClose={() => (isDeleting ? null : deleteAmenity(i))}
              >
                {tag.amenity}
              </InputChip>
              <Box css={{ marginBottom: '400' }} />
            </li>
          ))}
        </ul>
      )}

      {Boolean(!isInputFormDisabled || (!isPropertyAmenitiesUpdateRestricted && isFeed)) && (
        <Box>
          <Label htmlFor="additionalAmenitiesInput">
            <FormHelp css={{ paddingBottom: '100' }}>Describe amenity</FormHelp>
          </Label>
          <Box css={{ display: 'flex', gap: '400' }}>
            <Input
              size="sm"
              placeholder="Example: Pet area"
              onChange={addNewTagOnChangeHandler}
              onKeyUp={handleCreateAmenityKeyUp}
              onBlur={createAmenityOnBlurHandler}
              value={tagToAdd}
              error={!!(duplicateTagError || errorObj.additionalAmenities)}
              id="additionalAmenitiesInput"
              aria-describedby={
                duplicateTagError || errorObj.additionalAmenities ? 'additionalAmenitiesError' : undefined
              }
              fluid
              css={{
                maxWidth: '340px',
                '&::placeholder': {
                  fontSize: '14px',
                },
                md: {
                  width: '75%',
                },
              }}
              aria-invalid={duplicateTagError || errorObj.additionalAmenities ? 'true' : undefined}
            />

            <Box css={{ display: 'flex', flexDirection: 'row', gap: '200' }}>
              <Button
                size="sm"
                tone="brand"
                emphasis="outlined"
                onClick={createAmenity}
                onBlur={createAmenityOnBlurHandler}
                disabled={!tagToAdd}
                data-testid="AdditionalAmenities-AddButton"
                aria-label={`Add ${tagToAdd || 'amenity'} to property`}
                icon={<IconPlusCircleFilled />}
              >
                Add
              </Button>

              {isFeed && (
                <Popover
                  header={
                    <Heading level={2} textStyle="body-lg-bold">
                      {ADD_BUTTON_TOOLTIP_CONTENT.title}
                    </Heading>
                  }
                  body={<Paragraph>{ADD_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                  placement="top"
                />
              )}
            </Box>
          </Box>

          <VisuallyHiddenScreenReader errorMessage={errorObj.additionalAmenities} successMessage={successMessage} />
        </Box>
      )}
      {(duplicateTagError && (
        <FormHelp id="additionalAmenitiesError" error={true}>
          Unable to save duplicate tags
        </FormHelp>
      )) ||
        (errorObj.additionalAmenities && (
          <FormHelp id="additionalAmenitiesError" error={!!errorObj.additionalAmenities}>
            {errorObj.additionalAmenities}
          </FormHelp>
        ))}
      <Box css={{ marginBlock: '600' }} />
    </FieldSet>
  );
};

export default AdditionalAmenities;
